import type { ReactNode } from "react";

import { getLogger } from "@expert/logging";
import { Box, Flex } from "@mantine/core";
import { withErrorBoundary } from "react-error-boundary";

import type { AssistTabProps } from "@/sidecar/AssistTab";

import { ExpertHub } from "@/expert-hub";
import { UI_ORCHESTRATION_COMPONENT } from "@/expert-hub/data/expertHubErrorMetric";
import { HubRightPanel } from "@/expert-hub/right-panel/HubRightPanel";
import { trackedBoundaryProps } from "@/shared-utils";

const ExpertHubHandled = withErrorBoundary(
    ExpertHub,
    trackedBoundaryProps(getLogger({ module: "ExpertHub" }), UI_ORCHESTRATION_COMPONENT),
);
const HubRightPanelHandled = withErrorBoundary(
    HubRightPanel,
    trackedBoundaryProps(getLogger({ module: "HubRightPanel" }), UI_ORCHESTRATION_COMPONENT),
);

interface MainSlotProps {
    showExpertHub: boolean;
    /** What fills the main area on a call (transcript / chat timeline / EU / training). */
    onCallContent: ReactNode;
}

/**
 * Left-bottom (main) slot for the redesigned voice/chat workspaces: the on-call content, or the
 * Expert Hub (the off-call home) covering it when off call. The hub is absolutely positioned and
 * opaque, so it sits over the on-call content beneath. Shared so both experiences behave identically.
 */
export function WorkspaceMainSlot({ showExpertHub, onCallContent }: MainSlotProps) {
    return (
        <>
            {!showExpertHub && onCallContent}
            {showExpertHub && (
                <Flex pos="absolute" inset={0}>
                    <ExpertHubHandled />
                </Flex>
            )}
        </>
    );
}

interface RightSlotProps {
    showExpertHub: boolean;
    /** Right-top content on a call (Sidecar, or null). */
    onCallContent: ReactNode;
    /** The off-call session's guide for the hub's AI Assist view. */
    offCallGuide?: AssistTabProps;
}

/**
 * Right-top slot for the redesigned voice/chat workspaces: the off-call panel when off call, the
 * on-call Sidecar otherwise. The wrapper fills the slot and is a positioning context for the
 * absolutely-positioned Sidecar / HubRightPanel.
 */
export function WorkspaceRightSlot({ showExpertHub, onCallContent, offCallGuide }: RightSlotProps) {
    return (
        <Box h="100%" pos="relative">
            {showExpertHub ? <HubRightPanelHandled {...offCallGuide} /> : onCallContent}
        </Box>
    );
}
